import type { SessionInfo } from '@fdpf/shared';
import { api, ApiError } from './api.js';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

export function getSession(): Promise<SessionInfo> {
  return api.get<SessionInfo>('/api/session');
}

export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/**
 * Polls the session so a revoked device stops showing photos.
 *
 * Revocation cannot reach media the device has already cached — that is inherent to caching, and the
 * whole point of the precache design. The heartbeat is the mitigation: on a 401 we wipe the local
 * media cache and send the frame back to /join, so a device removed from the family stops displaying
 * within one interval rather than forever.
 */
export function startHeartbeat(onRevoked: () => void): () => void {
  let stopped = false;

  const tick = async () => {
    if (stopped) return;
    try {
      await api.get('/api/session/heartbeat');
    } catch (err) {
      if (isUnauthenticated(err)) {
        stopped = true;
        onRevoked();
      }
      // Network errors are expected on a flaky link and must not log the frame out.
    }
  };

  const timer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
  // Coming back from sleep is the most likely moment for a revocation to have happened.
  const onVisible = () => {
    if (!document.hidden) void tick();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
